/* =====================================================================
   YourMedicals — Blog article behaviour
   - Builds the "On this page" rail from the article's <h2>s
   - Scroll-spy highlights the current section
   - Calculates + injects reading time
   - Upgrades the raw FAQ (<h3>+<p>) markup into <details> accordions
   - Injects FAQPage + MedicalWebPage JSON-LD schema (SEO + GEO)
   No dependencies. Namespaced. Safe to defer.
   ===================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('.ym-article');
  if (!root) return;

  var content = root.querySelector('.ym-article__content');
  if (!content) return;

  /* ------------------------------------------------------------------
     1. READING TIME  (words / 220 wpm, rounded up, min 1)
     Fills any [data-ym-reading-time] element with "N min read".
     ------------------------------------------------------------------ */
  (function readingTime() {
    var target = root.querySelector('[data-ym-reading-time]');
    if (!target) return;
    var words = (content.textContent || '').trim().split(/\s+/).length;
    var mins = Math.max(1, Math.ceil(words / 220));
    target.textContent = mins + ' min read';
  })();

  /* ------------------------------------------------------------------
     2. FAQ UPGRADE  — turn <section class="article-faq"> h3+p pairs
     into <details>/<summary> accordions. The article bodies ship as
     plain h3 (question) + following block(s) (answer); this rebuilds
     them so the CSS accordion works and keyboard/native semantics hold.
     Idempotent: skips if already upgraded.
     ------------------------------------------------------------------ */
  var faqData = [];
  (function upgradeFaq() {
    var faq = root.querySelector('.article-faq');
    if (!faq || faq.getAttribute('data-ym-upgraded')) return;

    var nodes = Array.prototype.slice.call(faq.children);
    var i, first = true;

    nodes.forEach(function (node) {
      if (node.tagName !== 'H3') return;

      var question = node.textContent.trim();
      var answerNodes = [];
      var sib = node.nextElementSibling;
      while (sib && sib.tagName !== 'H3') {
        answerNodes.push(sib);
        sib = sib.nextElementSibling;
      }

      var details = document.createElement('details');
      if (first) { details.open = true; first = false; }

      var summary = document.createElement('summary');
      summary.textContent = question;
      details.appendChild(summary);

      var answerText = '';
      answerNodes.forEach(function (a) {
        answerText += ' ' + a.textContent.trim();
        details.appendChild(a); // move answer into <details>
      });

      node.parentNode.insertBefore(details, node);
      node.parentNode.removeChild(node); // remove the bare <h3>

      faqData.push({ q: question, a: answerText.trim() });
    });

    faq.setAttribute('data-ym-upgraded', '1');
  })();

  /* ------------------------------------------------------------------
     3. TABLE OF CONTENTS  — build the sticky rail from content <h2>s.
     Uses :scope > h2 so only the article's own section headings are
     listed — the FAQ, References and closing-CTA h2s live inside their
     own sections/aside, so they're excluded (matches the design rail).
     ------------------------------------------------------------------ */
  (function buildToc() {
    var rail = root.querySelector('.ym-article__rail-list');
    if (!rail) return;

    var headings = content.querySelectorAll(':scope > h2');
    if (!headings.length) {
      var railWrap = root.querySelector('.ym-article__rail');
      if (railWrap) railWrap.style.display = 'none';
      return;
    }

    var slug = function (s) {
      return s.toLowerCase().trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
    };

    var links = [];
    headings.forEach(function (h, idx) {
      if (!h.id) h.id = 'sec-' + (slug(h.textContent) || idx);
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = '#' + h.id;
      a.textContent = h.textContent;
      li.appendChild(a);
      rail.appendChild(li);
      links.push({ a: a, target: h });
    });

    /* smooth scroll */
    links.forEach(function (l) {
      l.a.addEventListener('click', function (e) {
        e.preventDefault();
        l.target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        history.replaceState(null, '', '#' + l.target.id);
      });
    });

    /* ---- scroll-spy via IntersectionObserver ---- */
    if ('IntersectionObserver' in window) {
      var setActive = function (id) {
        links.forEach(function (l) {
          l.a.classList.toggle('is-active', l.target.id === id);
        });
      };
      var visible = {};
      var obs = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          visible[en.target.id] = en.isIntersecting;
        });
        // topmost currently-visible heading wins
        for (var k = 0; k < links.length; k++) {
          if (visible[links[k].target.id]) { setActive(links[k].target.id); break; }
        }
      }, { rootMargin: '-80px 0px -70% 0px', threshold: 0 });

      links.forEach(function (l) { obs.observe(l.target); });
      setActive(links[0].target.id);
    }
  })();

  /* ------------------------------------------------------------------
     4. SCHEMA (JSON-LD)  — FAQPage + MedicalWebPage.
     Reads article metadata from data-* attributes on .ym-article so the
     Liquid template can populate them from Shopify fields.
     GEO note: this is the structured data LLMs + AI Overviews consume.
     ------------------------------------------------------------------ */
  (function injectSchema() {
    var d = root.dataset;

    var graph = [];

    /* MedicalWebPage */
    var page = {
      "@context": "https://schema.org",
      "@type": "MedicalWebPage",
      "name": d.title || document.title,
      "headline": d.title || document.title,
      "url": d.url || location.href,
      "inLanguage": "en-GB",
      "publisher": {
        "@type": "Pharmacy",
        "name": "YourMedicals",
        "url": "https://yourmedicals.co.uk",
        "identifier": {
          "@type": "PropertyValue",
          "propertyID": "GPhC registration",
          "value": "9012314"
        }
      }
    };
    if (d.published) page.datePublished = d.published;
    if (d.reviewed) {
      page.dateModified = d.reviewed;
      page.lastReviewed = d.reviewed;
    }
    if (d.reviewer) {
      page.reviewedBy = {
        "@type": "Person",
        "name": d.reviewer,                    // "Habibah Hafeji"
        "jobTitle": d.reviewerRole || "Independent Prescriber",
        "identifier": d.reviewerGphc || undefined // "2210611"
      };
    }
    graph.push(page);

    /* FAQPage — only if we found FAQ pairs */
    if (faqData.length) {
      graph.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.map(function (f) {
          return {
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          };
        })
      });
    }

    var script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(graph.length === 1 ? graph[0] : graph);
    document.head.appendChild(script);
  })();

})();
